// Code generated by @hex-insights/frontend-generator; While this comment is present this file may be overwritten. If manual edits are made, remove this comment.
import React from "react";
import { LazyQueryHookOptions } from "@apollo/client";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, ClassNameProps, Column, If, makeClassName, Modal, Row, StyleProps } from "@hex-insights/core";
import {
	FieldDisplayArgs,
	FormValues,
	MultiSelectField,
	MultiSelectFieldProps,
	SelectField,
	SelectFieldProps,
} from "@hex-insights/forms";
import {
	PaymentLineItem,
	PaymentLineItemSelectQuery,
	PaymentLineItemSelectQueryVariables,
	usePaymentLineItemSelectLazyQuery,
} from "../../../Utilities";
import { PaymentLineItemSelect } from "../../FormUtilities";
import { PaymentLineItemLink } from "../../Links";
import { PaymentLineItemOrderFormValues } from "../../Order";
import { ControlledCreate, ControlledCreateProps } from "./Form";
import styles from "./select.module.css";

export type SelectProps<FV extends FormValues> = Omit<
	SelectFieldProps<FV, PaymentLineItem["id"] | null>,
	"options" | "isLoading" | "blankValue" | "className" | "style"
> & {
	currentInstance?: PaymentLineItemSelect.ModelForOption | null;
	queryOptions?: LazyQueryHookOptions<PaymentLineItemSelectQuery, PaymentLineItemSelectQueryVariables>;
	load?: boolean;
	withCreate?: boolean;
	fieldClassName?: string;
	fieldStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Select field for the PaymentLineItem model.
 */
export function Select<FV extends FormValues>({
	formState,
	name,
	currentInstance,
	display = displayPaymentLineItem,
	queryOptions,
	load,
	withCreate = false,
	fieldClassName,
	fieldStyle,
	className,
	style,
	...props
}: SelectProps<FV>) {
	const defaultOrder = IndexForms.useOrderInput(PaymentLineItemOrderFormValues.initial);
	const [loadOptions, { loading, data, refetch }] = usePaymentLineItemSelectLazyQuery({
		...queryOptions,
		variables: {
			...queryOptions?.variables,
			order: queryOptions?.variables?.order ?? defaultOrder,
		},
		notifyOnNetworkStatusChange: true,
	});
	const isEditing = formState.formEditing[name];
	React.useEffect(() => {
		if (load || (load !== false && isEditing)) {
			loadOptions();
		}
	}, [load, isEditing, loadOptions]);
	const options = React.useMemo(
		() => PaymentLineItemSelect.toOptions(data?.paymentLineItemConnection.edges, currentInstance),
		[data, currentInstance],
	);
	const refetchOptions = React.useCallback(() => refetch(), [refetch]);

	const setValue = formState.formSetFunctions[name] as (action: React.SetStateAction<string | null>) => void;
	const onCreateSuccess = React.useCallback<ControlledCreateProps["onSuccess"]>(
		(_formState, result) => {
			setValue(result.id);
		},
		[setValue],
	);

	return (
		<Column
			justify="spaced-start"
			align="flex-end"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["select"], className)}
			style={style}
		>
			<SelectField
				formState={formState}
				name={name}
				isLoading={loading}
				options={options}
				display={display}
				blankValue={null}
				refetchOptions={refetchOptions}
				footerElement={
					withCreate ? (
						<Row justify="flex-end" style={{ padding: "0.25rem 0.75rem 0.5rem" }}>
							<PaymentLineItemCreateController onSuccess={onCreateSuccess} />
						</Row>
					) : null
				}
				className={makeClassName(styles["select__field"], fieldClassName)}
				style={fieldStyle}
				{...props}
			/>

			<If condition={isEditing && withCreate}>
				<PaymentLineItemCreateController onSuccess={onCreateSuccess} />
			</If>
		</Column>
	);
}

export type MultiSelectProps<FV extends FormValues> = Omit<
	MultiSelectFieldProps<FV, PaymentLineItem["id"]>,
	"options" | "isLoading" | "className" | "style"
> & {
	currentInstances?: PaymentLineItemSelect.ModelForOption[];
	queryOptions?: LazyQueryHookOptions<PaymentLineItemSelectQuery, PaymentLineItemSelectQueryVariables>;
	load?: boolean;
	withCreate?: boolean;
	fieldClassName?: string;
	fieldStyle?: React.CSSProperties;
} & Partial<ClassNameProps & StyleProps>;

/**
 * Multi-select field for the PaymentLineItem model.
 */
export function MultiSelect<FV extends FormValues>({
	formState,
	name,
	currentInstances,
	displayInstance = displayPaymentLineItem,
	queryOptions,
	load,
	withCreate = false,
	fieldClassName,
	fieldStyle,
	className,
	style,
	...props
}: MultiSelectProps<FV>) {
	const defaultOrder = IndexForms.useOrderInput(PaymentLineItemOrderFormValues.initial);
	const [loadOptions, { loading, data, refetch }] = usePaymentLineItemSelectLazyQuery({
		...queryOptions,
		variables: {
			...queryOptions?.variables,
			order: queryOptions?.variables?.order ?? defaultOrder,
		},
		notifyOnNetworkStatusChange: true,
	});
	const isEditing = formState.formEditing[name];
	React.useEffect(() => {
		if (load || (load !== false && isEditing)) {
			loadOptions();
		}
	}, [load, isEditing, loadOptions]);
	const options = React.useMemo(
		() => PaymentLineItemSelect.toMultiOptions(data?.paymentLineItemConnection.edges, currentInstances),
		[data, currentInstances],
	);
	const refetchOptions = React.useCallback(() => refetch(), [refetch]);

	const setValue = formState.formSetFunctions[name] as (action: React.SetStateAction<string[]>) => void;
	const onCreateSuccess = React.useCallback<ControlledCreateProps["onSuccess"]>(
		(_formState, result) => {
			setValue((prev) => [...prev, result.id]);
		},
		[setValue],
	);

	return (
		<Column
			justify="spaced-start"
			align="flex-end"
			verticalSpacing="0.25rem"
			className={makeClassName(styles["multi-select"], className)}
			style={style}
		>
			<MultiSelectField
				formState={formState}
				name={name}
				isLoading={loading}
				options={options}
				displayInstance={displayInstance}
				refetchOptions={refetchOptions}
				footerElement={
					withCreate ? (
						<Row justify="flex-end" style={{ padding: "0.25rem 0.75rem 0.5rem" }}>
							<PaymentLineItemCreateController onSuccess={onCreateSuccess} />
						</Row>
					) : null
				}
				className={makeClassName(styles["multi-select__field"], fieldClassName)}
				style={fieldStyle}
				{...props}
			/>

			<If condition={isEditing && withCreate}>
				<PaymentLineItemCreateController onSuccess={onCreateSuccess} />
			</If>
		</Column>
	);
}

function displayPaymentLineItem({ value: id, formattedValue }: FieldDisplayArgs<string | null>) {
	if (id === null) {
		return formattedValue;
	}
	return <PaymentLineItemLink instance={{ id }}>{formattedValue}</PaymentLineItemLink>;
}

type PaymentLineItemCreateControllerProps = Pick<ControlledCreateProps, "onSuccess">;

function PaymentLineItemCreateController({ onSuccess }: PaymentLineItemCreateControllerProps) {
	const { isModalOpen, toggleIsModalOpen, closeModal } = Modal.useToggle();

	const addToCache = PaymentLineItemSelect.useAddToCache();
	const fullOnSuccess = React.useCallback<ControlledCreateProps["onSuccess"]>(
		(formState, result) => {
			addToCache(result);
			onSuccess(formState, result);
			closeModal();
		},
		[addToCache, onSuccess, closeModal],
	);

	return (
		<React.Fragment>
			<Button variant="link" size="small" onClick={toggleIsModalOpen}>
				Create New
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal size="small" onClose={toggleIsModalOpen}>
					<Modal.Body>
						<ControlledCreate onSuccess={fullOnSuccess} />
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}
